import React, { useEffect, useState } from 'react'

import {
  PIICategoryTypeParametersList,
  addOrUpdatePIICategoryTypeParam,
} from '../../../assets/ts/data/pii/pii-category-type-parameters'
import { defaultToEmptyString } from '../../../assets/ts/formatting/formatting'
import Buttons from '../../../components/buttons/buttons'
import usePIICategoryParameterFormatsMetadata from '../../../hooks/pii/use-pii-category-parameter-format-metadata'
import usePiiCategoryTypeParametersMetadata from '../../../hooks/pii/use-pii-category-type-parameters-metadata'
import { SEVERITY_OPTIONS } from '../../../utils/constants/global-constants'
import InputSelect from '../../../utils/controls/input-select'
import InputSwitch from '../../../utils/controls/input-switch'
import ConfigurationFormat from './configuration-format'

interface Props {
  paramMode: 2 | 3
  showLoaderAdd: boolean
}

const DefinedFormats = ({ paramMode, showLoaderAdd }: Props) => {
  const [clickedPII, setClickedPII] = useState<number>(-1)
  const [editingId, setEditingId] = useState<number>(-1)
  const [editCategories, setEditCategories] = useState<PIICategoryTypeParametersList[]>([
    {
      CID: -1,
      CategoryContext: '',
      CategoryParamName: '',
      ParamAllowedDataTypes: '',
      ParamEnable: '',
      ParamMode: 3, // 2 === Pre - Defined Categories 3 === User - Defined Categories
      Severity: '',
      Title: '',
      eKey: '',
      eTag: '',
    },
  ])

  const { fetchPiiCategoryTypeParameterMetadata, piiCategoryTypeParameterMetadata } =
    usePiiCategoryTypeParametersMetadata({
      isArchivedFilter: null,
      paramModeFilter: paramMode,
    })
  const { fetchPiiCategoryParameterFormatsMetadata, piiCategoryParameterFormatsMetadata } =
    usePIICategoryParameterFormatsMetadata({
      isArchivedFilter: null,
    })

  const togglePIIDropDown = (index: number, value: any) => {
    if (clickedPII === index && value === 'expanded') {
      return setClickedPII(-1)
    } else if (value !== 'expanded') {
      return setClickedPII(index)
    }
    return setClickedPII(index)
  }
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number, value: string) => {
    if (e.key === 'Enter') {
      togglePIIDropDown(index, value)
    }
  }

  const handleTextChange = (id: number | undefined, value: string, newCategory: string) => {
    setEditCategories((prevTexts) =>
      prevTexts.map((category) =>
        category.CID === id ? { ...category, [value]: newCategory } : category
      )
    )
  }
  const handleEditCategory = async (id: number | undefined) => {
    setEditingId(id)
  }
  const handleSaveCategory = async (category) => {
    const update = await addOrUpdatePIICategoryTypeParam({
      ...category,
      eKey: category.eKey ? category.eKey : '',
      eTag: category.eTag ? category.eTag : '',
    })
    setEditingId(-1)
    fetchPiiCategoryTypeParameterMetadata()
  }

  const handleCancel = () => {
    setEditingId(-1)
    fetchPiiCategoryTypeParameterMetadata()
  }

  useEffect(() => {
    setEditCategories(piiCategoryTypeParameterMetadata)
  }, [piiCategoryTypeParameterMetadata])

  useEffect(() => {
    fetchPiiCategoryTypeParameterMetadata()
  }, [showLoaderAdd])

  return (
    <>
      {editCategories.map((category, key) => (
        <div
          aria-live='polite'
          className='piiaccordionlist bordered2 border-radius'
          key={category.CID}
          onKeyDown={(e) =>
            handleKeyDown(e, category.CID, clickedPII === category.CID ? 'expanded' : 'collapsed')
          }
          tabIndex={0}
        >
          {editingId === category.CID ? (
            <div className='d-flex align-items-center px-2 py-1 piiaccordionlistheader'>
              <div
                className={
                  clickedPII === category.CID
                    ? 'icon-arrow-down font-14'
                    : 'icon-arrow-down font-14 rotate180'
                }
              ></div>
              <div className='d-flex form-xs'>
                <input
                  onChange={(e) =>
                    handleTextChange(category.CID, 'CategoryParamName', e.target.value)
                  }
                  type='CategoryParamName'
                  value={category.CategoryParamName}
                />
                <div className='dividerdashed vertical mx-2'></div>
                <InputSelect
                  className='form-sm w-100 font-13 sourcesanspro'
                  formClassName='form-vertical w-100'
                  inputProps={{
                    className: 'font-12 poppins-medium title-color',
                    id: 'severity',
                    name: '',
                    value: category.Severity,
                  }}
                  isMandatory
                  label=''
                  onChange={(e) => handleTextChange(category.CID, 'Severity', e.target.value)}
                  options={SEVERITY_OPTIONS}
                  type='Severity'
                  value={category.Severity}
                />
              </div>
              <div className='ms-auto d-flex gap-1'>
                <Buttons
                  aria-label='Save'
                  className='btn-xs font-0 btn-border text-white bgcolor-primary border-radius'
                  icon='icon-checked font-11'
                  label='Save'
                  onClick={() => handleSaveCategory(category)}
                />

                <Buttons
                  aria-label='Cancel'
                  className='btn-xs font-0 btn-border2 bgcolor-5 darktext  border-radius'
                  icon='icon-close font-10'
                  label='Cancel'
                  onClick={handleCancel}
                />
              </div>
            </div>
          ) : (
            <>
              <div
                className={
                  clickedPII === category.CID
                    ? 'd-flex px-2 py-1 piiaccordionlistheader  active'
                    : 'd-flex piiaccordionlistheader px-2 py-1'
                }
              >
                <div
                  className='d-flex w-100 align-items-center'
                  onClick={() =>
                    togglePIIDropDown(
                      category.CID,
                      clickedPII === category.CID ? 'expanded' : 'collapsed'
                    )
                  }
                >
                  <div
                    className={
                      clickedPII === category.CID
                        ? 'icon-arrow-down font-14 me-2 color-primary bold'
                        : 'icon-arrow-down font-14 me-2 rotate-90 color-primary bold'
                    }
                  ></div>
                  <div className='poppins-meduim font-12 title-color20'>
                    {defaultToEmptyString(category.CategoryParamName)}
                  </div>
                  <div className='dividerdashed vertical mx-2'></div>
                  <div className={`severity ${category?.Severity}`}>
                    Severity: {defaultToEmptyString(category.Severity)}
                  </div>
                </div>
                <Buttons
                  aria-label='Edit'
                  className='btn-xs font-0 color-primary editbtn ms-auto me-2'
                  icon='icon-pencil font-14 color-primary'
                  label='Edit'
                  onClick={() => handleEditCategory(category.CID)}
                />
                <InputSwitch
                  Enable={category?.ParamEnable === 'Yes'}
                  allFormats={piiCategoryParameterFormatsMetadata}
                  categories={editCategories}
                  cid={category.CID}
                  fetchPiiCategoryParameterFormatsMetadata={
                    fetchPiiCategoryParameterFormatsMetadata
                  }
                  fetchPiiCategoryTypeParameterMetadata={fetchPiiCategoryTypeParameterMetadata}
                />
              </div>

              <div
                className={
                  clickedPII === category.CID
                    ? 'configurationlists border-top2 border-bottom2 pb-2 active'
                    : 'configurationlists pb-2'
                }
              >
                <ConfigurationFormat
                  categoryIsEnabled={category?.ParamEnable === "Yes"}
                  parentId={category.CID}
                />
              </div>
            </>
          )}
        </div>
      ))}
    </>
  )
}

export default DefinedFormats
